// /parent/absences — calm management page for absence/late notices.
//
// Shows: upcoming + recent notices for all children in this family,
// with a single Cancel button per submitted row.
//
// CTA at top: "Report absence" / "Running late" — opens the shared
// AttendanceNoticeSheet. Mobile-first vertical card layout.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CalendarBlank,
  Clock,
  Megaphone,
  ProhibitInset,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  cancelNotice,
  formatLocalTime,
  formatNoticeDate,
  listFamilyNotices,
  NOTICE_LABELS,
} from "@/lib/attendanceNotices";
import { todayStr } from "@/lib/attendance";
import { listChildrenForParent } from "@/lib/dailyReports";
import AttendanceNoticeSheet from "@/components/attendance/AttendanceNoticeSheet";
import ChildAvatar from "@/components/children/ChildAvatar";
import { isSupabaseConfigured } from "@/lib/supabase";

export default function ParentAbsences() {
  const { profile } = useAuth();
  const [children, setChildren] = useState([]);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState({ open: false, child: null, type: "absent" });

  const refresh = async () => {
    if (!profile?.id || !isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const kids = await listChildrenForParent(profile.id);
      setChildren(kids || []);
      const fid = kids?.[0]?.family_id || null;
      if (fid) {
        const rows = await listFamilyNotices({ familyId: fid });
        setNotices(rows);
      }
    } catch (err) {
      toast.error(err.message || "Could not load notices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const today = todayStr();
  const upcoming = useMemo(
    () =>
      notices.filter(
        (n) => n.notice_date >= today && n.status === "submitted",
      ),
    [notices, today],
  );
  const past = useMemo(
    () => notices.filter((n) => !(n.notice_date >= today && n.status === "submitted")),
    [notices, today],
  );

  const onOpenSheet = (type, child) =>
    setSheet({ open: true, child: hydrateChild(child, children), type });

  const onCancel = async (noticeId) => {
    if (!window.confirm("Cancel this notice?")) return;
    try {
      await cancelNotice(noticeId);
      toast.success("Notice cancelled");
      refresh();
    } catch (err) {
      toast.error(err.message || "Could not cancel");
    }
  };

  const onSubmitted = () => refresh();

  return (
    <div data-testid="parent-absences" className="space-y-5">
      <div>
        <Link
          to="/parent"
          data-testid="parent-absences-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" />
          Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Absences & late notices
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Let your child's teacher know in advance — or right now. Notices
          show up on the classroom roster the moment you submit.
        </p>
      </div>

      {/* Quick CTAs per child (or one if single child). */}
      <section className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-[0_2px_12px_rgba(20,22,26,0.04)]">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          File a notice
        </p>
        <ul
          data-testid="parent-absences-children"
          className="mt-2 space-y-2"
        >
          {children.length === 0 && !loading && (
            <li className="text-sm text-neutral-500">
              No children linked to your family yet.
            </li>
          )}
          {children.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-neutral-50/40 p-3"
            >
              <ChildAvatar
                path={c.profile_photo_path}
                name={`${c.first_name || ""} ${c.last_name || ""}`}
                size={36}
              />
              <p className="font-heading text-sm font-semibold text-neutral-900">
                {c.first_name} {c.last_name || ""}
              </p>
              <div className="ml-auto flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid={`parent-report-absent-${c.id}`}
                  onClick={() => onOpenSheet("absent", c)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#C6851E]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5"
                >
                  <ProhibitInset size={12} weight="duotone" />
                  Report absence
                </button>
                <button
                  type="button"
                  data-testid={`parent-report-late-${c.id}`}
                  onClick={() => onOpenSheet("late", c)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                >
                  <Clock size={12} weight="duotone" />
                  Running late
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Upcoming notices */}
      <section>
        <h2 className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
          Upcoming · awaiting school
        </h2>
        <NoticeList
          rows={upcoming}
          empty="No upcoming notices."
          onCancel={onCancel}
          testIdPrefix="parent-notice-upcoming"
          showCancel
        />
      </section>

      {/* Past + cancelled + superseded */}
      <section>
        <h2 className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">
          Recent
        </h2>
        <NoticeList
          rows={past}
          empty="Nothing in the last two weeks."
          testIdPrefix="parent-notice-recent"
        />
      </section>

      <AttendanceNoticeSheet
        open={sheet.open}
        onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))}
        initialType={sheet.type}
        child={sheet.child}
        submittedBy={profile?.id}
        onSubmitted={onSubmitted}
      />
    </div>
  );
}

function NoticeList({ rows, empty, onCancel, showCancel, testIdPrefix }) {
  if (!rows || rows.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
        {empty}
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((n) => (
        <li
          key={n.id}
          data-testid={`${testIdPrefix}-${n.id}`}
          className="flex items-start gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
        >
          <NoticeIcon type={n.notice_type} status={n.status} />
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-neutral-900">
              {n.child?.first_name} {n.child?.last_name || ""} ·{" "}
              {NOTICE_LABELS[n.notice_type]}
              {n.notice_type === "late" && n.expected_arrival_time
                ? ` · ${formatLocalTime(n.expected_arrival_time)}`
                : ""}
            </p>
            <p className="mt-0.5 text-[12px] text-neutral-600">
              {formatNoticeDate(n.notice_date)} ·{" "}
              <NoticeStatusLabel status={n.status} />
              {n.reason ? ` · "${n.reason}"` : ""}
            </p>
          </div>
          {showCancel && n.status === "submitted" && (
            <button
              type="button"
              data-testid={`parent-notice-cancel-${n.id}`}
              onClick={() => onCancel?.(n.id)}
              className="shrink-0 rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function NoticeIcon({ type, status }) {
  const Icon =
    type === "absent" ? ProhibitInset : type === "late" ? Clock : Megaphone;
  const color =
    status !== "submitted"
      ? "bg-neutral-100 text-neutral-500"
      : type === "absent"
        ? "bg-[#C6851E]/10 text-[#8A5A12]"
        : "bg-amber-50 text-amber-800";
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${color}`}
      aria-hidden
    >
      <Icon size={18} weight="duotone" />
    </span>
  );
}

function NoticeStatusLabel({ status }) {
  if (status === "submitted") return <em className="not-italic text-primary-hover">Sent</em>;
  if (status === "cancelled") return <em className="not-italic text-neutral-500">Cancelled</em>;
  if (status === "superseded")
    return <em className="not-italic text-neutral-500">Recorded by teacher</em>;
  return status;
}

function hydrateChild(c, all) {
  // Merge in family_id / classroom_id / organization_id so the sheet
  // has everything it needs to insert.
  if (!c) return null;
  const found = (all || []).find((x) => x.id === c.id) || c;
  return {
    id: found.id,
    first_name: found.first_name,
    last_name: found.last_name,
    family_id: found.family_id,
    classroom_id: found.classroom_id,
    organization_id: found.organization_id,
    profile_photo_path: found.profile_photo_path,
  };
}
